import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import {
  GetNursingStationById,
  UpdateNursingStation
} from "../../../../api/ipd/nursingStationAPI";
import { GetDepartments } from "../../../../api/masters/departmentAPI";
import {
  GetDepartmentalSubSpecialities,
  GetDepartmentalSubSpecialityById,
  UpdateDepartmentalSubSpeciality
} from "../../../../api/diagnosis/master/departmentalSubSpecilityAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  departmentId: yup.string().required("Department is required"),
  creditAccount: yup.string().required("Credit Account is required"),
  reportStyle: yup.string().required("Report Style is required"),
  remarks: yup.string().nullable(),
  parentSubSpecialityId: yup.string().nullable()
});

const EditDepartmentalSubSpeciality = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();
  const [departments, setDepartments] = useState([]);
  const [subSpeciality, setSubSpeciality] = useState([]);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const fetchDepartments = async () => {
    try {
      const res = await GetDepartments();
      if (res.success) setDepartments(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  const fetchSubSpeciality = async () => {
    try {
      const res = await GetDepartmentalSubSpecialities();
      if (res.success)
        setSubSpeciality(res.data.filter(item => item._id != id));
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  const fetchData = async () => {
    try {
      const response = await GetDepartmentalSubSpecialityById(id); // Make sure login function returns token

      if (response.success) {
        Object.keys(response.data).forEach(key => {
          setValue(key, response.data[key]);
        });
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  // Fetch existing data
  useEffect(
    () => {
      if (id) {
        fetchDepartments().then(() =>
          fetchSubSpeciality().then(() => fetchData())
        );
      }
    },
    [id, setValue]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await UpdateDepartmentalSubSpeciality(id, data);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb
        parent="Departmental Sub Speciality"
        title="Edit Departmental Sub Speciality"
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <h5>Edit Departmental Sub Speciality</h5>
                  <Link
                    to={`/diagnosis/master/departmental-sub-speciality/list`}
                  >
                    <button className="btn btn-primary" id="list">
                      List Departmental Sub Speciality
                    </button>
                    <UncontrolledTooltip placement="top" target="list">
                      List Departmental Sub Speciality
                    </UncontrolledTooltip>
                  </Link>
                </div>
              </div>
              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-6">
                      <label htmlFor="name">Sub Speciality</label>
                      <input
                        className="form-control"
                        id="name"
                        type="text"
                        placeholder="Sub Speciality"
                        {...register("name")}
                      />
                      {errors.name &&
                        <p className="text-danger">
                          {errors.name.message}
                        </p>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Department *</label>
                      <select
                        className="form-control"
                        {...register("departmentId")}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dep =>
                          <option key={dep._id} value={dep._id}>
                            {dep.name}
                          </option>
                        )}
                      </select>
                      {errors.departmentId &&
                        <p className="text-danger">
                          {errors.departmentId.message}
                        </p>}
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="creditAccount">Credit Account</label>
                        <input
                          className="form-control"
                          id="creditAccount"
                          placeholder="Credit Account"
                          {...register("creditAccount")}
                        />
                        {errors.creditAccount &&
                          <p className="text-danger">
                            {errors.creditAccount.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Report Style *</label>
                      <select
                        className="form-control"
                        {...register("reportStyle")}
                      >
                        <option value="">Select Report Style</option>
                        <option value="DESCRIPTIVE">Descriptive</option>
                        <option value="PATHOLOGY">Pathology</option>
                        <option value="TABULAR">Tabular</option>
                        <option value="QUANTITATIVE">Quantitative</option>
                        <option value="QUALITATIVE">Qualitative</option>
                        <option value="SEMI_QUANTITATIVE">
                          Semi-Quantitative
                        </option>
                        <option value="MICROBIOLOGY">Microbiology</option>
                        <option value="SENSITIVITY">
                          Culture & Sensitivity
                        </option>
                        <option value="IMMUNOLOGY">Immunology</option>
                        <option value="MOLECULAR">Molecular / Genetic</option>
                        <option value="ULTRASOUND">Ultrasound</option>
                        <option value="RADIOLOGY">Radiology</option>
                        <option value="FLOW_CYTOMETRY">Flow Cytometry</option>
                        <option value="CYTOLOGY">Cytology</option>
                        <option value="STRUCTURED">Structured Template</option>
                      </select>
                      {errors.reportStyle &&
                        <p className="text-danger">
                          {errors.reportStyle.message}
                        </p>}
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="remarks">Remarks</label>
                        <input
                          className="form-control"
                          id="remarks"
                          placeholder="Remarks"
                          {...register("remarks")}
                        />
                        {errors.remarks &&
                          <p className="text-danger">
                            {errors.remarks.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Department *</label>
                      <select
                        className="form-control"
                        {...register("parentSubSpecialityId")}
                      >
                        <option value="">Parent Sub Speciality</option>
                        {subSpeciality.map(item =>
                          <option key={item._id} value={item._id}>
                            {item.name}
                          </option>
                        )}
                      </select>
                      {errors.parentSubSpecialityId &&
                        <p className="text-danger">
                          {errors.parentSubSpecialityId.message}
                        </p>}
                    </div>
                  </div>
                </div>
                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditDepartmentalSubSpeciality;
